import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { trackCtaClick } from '../analytics/ctaGoal';
import { Helmet } from 'react-helmet-async';
import RouteSeo from '../seo/RouteSeo';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { apiGet } from '../api';
import { formatUntil } from '../utils/formatUntil';
import VerifyEmailInline from '../components/VerifyEmailInline';
import { visitorCurrency, planPrice, annualPerMonth, monthsFree, PaidPlan } from '../pricing/prices';
import '../App.css';

const PAYMENT_LINKS: Record<PaidPlan, string> = {
  monthly: process.env.REACT_APP_STRIPE_MONTHLY_LINK || '',
  annual:  process.env.REACT_APP_STRIPE_ANNUAL_LINK || '',
};

const MONTHS_WORD: Record<number, string> = { 1: 'One month', 2: 'Two months', 3: 'Three months' };

// What the status check found, when it means the visitor must not reach checkout. (#885)
type Notice =
  | { kind: 'permanent' }
  | { kind: 'collaborator'; until: string }
  | { kind: 'cancelling'; until: string }
  | { kind: 'samePlan'; plan: PaidPlan }
  | { kind: 'otherPlan'; plan: PaidPlan; pressed: PaidPlan }
  | { kind: 'subscribed' };

const AccountLink = () => <Link to="/account">Account page</Link>;

const NoticeText: React.FC<{ notice: Notice }> = ({ notice }) => {
  switch (notice.kind) {
    case 'permanent':
      return <>Your account has permanent access, so there is nothing to buy.</>;
    case 'collaborator':
      return <>You have collaborator access until {notice.until}, so there is nothing to buy.</>;
    case 'cancelling':
      return <>Your subscription ends on {notice.until}. You can keep it with Resume subscription on your <AccountLink />.</>;
    case 'samePlan':
      return <>You already have the {notice.plan} plan, so there is nothing to buy. You can manage it on your <AccountLink />.</>;
    case 'otherPlan':
      return <>You are on the {notice.plan} plan. You can switch to {notice.pressed} on your <AccountLink />, without starting a second subscription.</>;
    case 'subscribed':
      return <>You already have a subscription. You can manage it on your <AccountLink />.</>;
  }
};

/** `onEmailVerified` tells App, so the Account page stops asking once the address is proved here. */
const PricingPage: React.FC<{ onEmailVerified: () => void }> = ({ onEmailVerified }) => {
  const navigate = useNavigate();
  // CAD for a Canadian time zone, else USD: what Stripe will most likely charge, which
  // it decides from the IP at checkout, hence the note under the cards. (#826)
  const [currency] = React.useState(visitorCurrency);
  const free = monthsFree(currency);
  const freeLabel = `${free} month${free === 1 ? '' : 's'} free`;
  const { authStatus } = useAuthenticator((context) => [context.authStatus]);
  const signedIn = authStatus === 'authenticated';
  const [needsVerification, setNeedsVerification] = React.useState(false);
  const [notice, setNotice] = React.useState<Notice | null>(null);
  // True while the status is being checked, so a second tap cannot start a second checkout.
  const [checkingOut, setCheckingOut] = React.useState(false);
  // Read synchronously, so a second press in the same tick is ignored too.
  const checkingOutRef = React.useRef(false);
  // The verify alert takes focus when it appears (the Subscribe button that had it is far
  // below), and the plan's Subscribe button takes it back once verified (#1130).
  const verifyRef = React.useRef<HTMLDivElement>(null);
  const annualBtnRef = React.useRef<HTMLButtonElement>(null);
  const monthlyBtnRef = React.useRef<HTMLButtonElement>(null);
  const chosenPlan = React.useRef<PaidPlan>('annual');
  React.useEffect(() => {
    if (needsVerification) verifyRef.current?.focus();
  }, [needsVerification]);

  const handlePurchase = async (plan: PaidPlan) => {
    if (authStatus !== 'authenticated') {
      trackCtaClick('pricing');
      navigate('/register');
      return;
    }
    // Ignored, not disabled: a disabled button would drop keyboard focus to the page body.
    if (checkingOutRef.current) return;
    checkingOutRef.current = true;
    chosenPlan.current = plan;
    setCheckingOut(true);
    // A sentence or alert from an earlier press must not outlive this one.
    setNotice(null);
    try {
      const session = await fetchAuthSession();
      const userId = session.tokens?.accessToken.payload.sub as string;

      // Someone who already has access must not be sent to pay again: a second checkout
      // starts a second subscription. Collaborators (#883) and paying accounts (#885) are
      // told why, and where to manage it. This comes before the verification check, so an
      // unverified one is told about their access rather than to verify "before subscribing".
      // A failed lookup falls through to checkout: blocking a real buyer on a transient
      // error is worse than a charge that can be refunded.
      try {
        const res = await apiGet('/subscriptions/status');
        const s = (await res.body.json()) as unknown as {
          plan?: string; active?: boolean; expiry?: number; permanent?: boolean;
          cancelAtPeriodEnd?: boolean; isTrial?: boolean;
        };
        const paid = s.plan === 'monthly' || s.plan === 'annual' ? s.plan : null;
        let found: Notice | null = null;
        if (s.permanent === true) {
          found = { kind: 'permanent' };
        } else if (s.plan === 'collaborator' && s.active && s.expiry) {
          found = { kind: 'collaborator', until: formatUntil(s.expiry) };
        } else if (s.active && paid && s.cancelAtPeriodEnd === true && s.expiry) {
          // Whichever button was pressed: the way to keep it is Resume, not a new checkout.
          found = { kind: 'cancelling', until: formatUntil(s.expiry) };
        } else if (s.active && paid) {
          found = paid === plan ? { kind: 'samePlan', plan: paid } : { kind: 'otherPlan', plan: paid, pressed: plan };
        } else if (s.active && !s.isTrial) {
          // Safety net: an unknown paid plan never reaches checkout.
          found = { kind: 'subscribed' };
        }
        if (found) {
          setNotice(found);
          setNeedsVerification(false);
          // The cards stack on a phone, so the notice can be screens above the button.
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
      } catch { /* fall through to checkout, as before */ }

      // Signup no longer proves the address (#697), and this is where it has to be
      // proved: receipts, billing disputes and account recovery all depend on us
      // being able to reach them. The form in the alert below does the verifying,
      // so send them to it rather than into Stripe.
      const isVerified = (s: typeof session) => {
        const c = s.tokens?.idToken?.payload ?? {};
        return c['email_verified'] === true || c['email_verified'] === 'true';
      };

      let verified = isVerified(session);
      if (!verified) {
        // The cached ID token can lag a verification done earlier in this session,
        // so confirm against a fresh one before refusing someone's money.
        try {
          verified = isVerified(await fetchAuthSession({ forceRefresh: true }));
        } catch { /* keep the cached answer */ }
      }

      if (!verified) {
        setNeedsVerification(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        // Already showing from an earlier press: the effect won't run again, so focus it here.
        verifyRef.current?.focus();
        return;
      }

      setNeedsVerification(false);
      window.location.href = `${PAYMENT_LINKS[plan]}?client_reference_id=${userId}`;
    } catch {
      trackCtaClick('pricing');
      navigate('/register');
    } finally {
      checkingOutRef.current = false;
      setCheckingOut(false);
    }
  };

  return (
    <>
    <RouteSeo path="/pricing" />
    <Helmet>
      <script type="application/ld+json">{JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          { "@type": "Question", "name": "What do I get during the Chaptery free trial?", "acceptedAnswer": { "@type": "Answer", "text": "Full access to every Chaptery feature — the desktop editor, world building, the relationship graph, the storyboard, the writing dashboard, the assistant, export and cloud sync — for one book, for 30 days. No credit card required. When you subscribe, you can create unlimited books." } },
          { "@type": "Question", "name": "What happens when my Chaptery trial ends?", "acceptedAnswer": { "@type": "Answer", "text": "The desktop app switches to read-only mode with export available so you can always access your work. Subscribe to unlock everything again. Your data is never deleted." } },
          { "@type": "Question", "name": "Which Chaptery subscription plan is right for me?", "acceptedAnswer": { "@type": "Answer", "text": "If you plan to use Chaptery long-term, the Annual plan at $99.99/year saves you two months compared to paying $9.99/month. If you prefer flexibility, the Monthly plan lets you cancel at any time." } },
          { "@type": "Question", "name": "Can I cancel my Chaptery subscription anytime?", "acceptedAnswer": { "@type": "Answer", "text": "Yes. Cancel from your account page and you keep full access through the end of your current billing period. No partial refunds, no cancellation fees." } }
        ]
      })}</script>
      <script type="application/ld+json">{JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Chaptery", "item": "https://trychaptery.com" },
          { "@type": "ListItem", "position": 2, "name": "Pricing", "item": "https://trychaptery.com/pricing" }
        ]
      })}</script>
    </Helmet>
    <div className="pricing-page container">
      <header className="page-header center">
        <h1>Simple &amp; Transparent Pricing</h1>
        {!signedIn && <p>Start free for 30 days. No credit card required.</p>}
      </header>

      {needsVerification && (
        <div className="feedback-banner feedback-error pricing-verify" ref={verifyRef} role="region" tabIndex={-1} aria-labelledby="pricing-verify-text">
          <p id="pricing-verify-text">Verify your email address before subscribing. We need a working address for your billing emails, and to get you back into your account if you lose your password.</p>
          <VerifyEmailInline onVerified={() => {
            setNeedsVerification(false);
            onEmailVerified();
            // The form and alert are going; the next step is the plan they chose.
            (chosenPlan.current === 'annual' ? annualBtnRef : monthlyBtnRef).current?.focus();
          }} />
        </div>
      )}

      {/* The live region is always mounted and only its contents change: one that arrives
          with its text already in it is announced unreliably. */}
      <div role="status" aria-live="polite">
        {notice && (
          <div className="feedback-banner feedback-success">
            <NoticeText notice={notice} />
          </div>
        )}
      </div>

      {/* Each card carries only what DIFFERS. Every plan gets the same product, so
          repeating a ten-item list three times said nothing new and made the page six
          screens tall on a phone — where 77% of visitors are. The shared set is stated
          once, below. (#790, #791) */}
      <div className={`pricing-grid ${signedIn ? 'pricing-grid--two' : 'pricing-grid--three'}`}>

        {/* Free Trial. A trial comes with a new account, so a signed-in visitor (in a trial
            or past one) is never offered it: "Start Free Trial" sent them through sign-up
            and back to the bookshelf preview, a loop (#1059). */}
        {!signedIn && (
        <div className="pricing-card advanced-data-panel">
          <div className="plan-badge">Free</div>
          <h3>Free Trial</h3>
          <p className="price">$0<span>/30 days</span></p>
          <p className="price-sub">no credit card required</p>
          <ul className="feature-list">
            <li className="included">Every feature below</li>
            {/* A limit, not a benefit — a green tick beside "One book" reads as a perk.
                "30 days" is dropped: the price line directly above already says it. */}
            <li className="partial">One book at a time</li>
          </ul>
          <button className="btn-secondary" onClick={() => { trackCtaClick('pricing'); navigate('/register'); }}>
            Start Free Trial
          </button>
        </div>
        )}

        {/* Annual Subscription — featured */}
        <div className="pricing-card advanced-data-panel featured">
          <div className="plan-badge featured-badge">Best Value</div>
          <h3>Cloud Annual</h3>
          <p className="price">{planPrice('annual', currency)}<span>/yr</span></p>
          <p className="price-sub">
            <span className="price-per-month">{annualPerMonth(currency)} / month</span>
            <span className="price-savings">{freeLabel}</span>
          </p>
          <ul className="feature-list">
            <li className="included">Every feature below</li>
            <li className="included">Unlimited books*</li>
            <li className="included">{MONTHS_WORD[free] ?? `${free} months`} free</li>
          </ul>
          <button className="btn-primary" ref={annualBtnRef} onClick={() => handlePurchase('annual')} aria-disabled={checkingOut} style={checkingOut ? { opacity: 0.6, cursor: 'default' } : undefined}>
            Subscribe Annually
          </button>
        </div>

        {/* Monthly Subscription */}
        <div className="pricing-card advanced-data-panel">
          <div className="plan-badge">Monthly</div>
          <h3>Cloud Monthly</h3>
          <p className="price">{planPrice('monthly', currency)}<span>/mo</span></p>
          <p className="price-sub">cancel anytime</p>
          <ul className="feature-list">
            <li className="included">Every feature below</li>
            <li className="included">Unlimited books*</li>
            <li className="included">Cancel any time</li>
          </ul>
          <button className="btn-secondary" ref={monthlyBtnRef} onClick={() => handlePurchase('monthly')} aria-disabled={checkingOut} style={checkingOut ? { opacity: 0.6, cursor: 'default' } : undefined}>
            Subscribe Monthly
          </button>
        </div>

      </div>

      {/* Both ways round. Stripe picks the currency by location and this page by time
          zone, so a Canadian on a US zone (travelling, or on a VPN) sees USD here and is
          charged CAD at checkout, and the reverse. Naming the other country's amounts
          means nobody meets a price at checkout that the page did not show them. Worded
          for subscribing, since it describes new checkouts. (#826) */}
      <p className="pricing-currency-note">
        {currency === 'cad'
          ? <>Prices in Canadian dollars. Subscribing from the US? You&rsquo;re billed in US dollars:{' '}
              {planPrice('monthly', 'usd')} a month or {planPrice('annual', 'usd')} a year.</>
          : <>Prices in US dollars. Subscribing from Canada? You&rsquo;re billed in Canadian dollars:{' '}
              {planPrice('monthly', 'cad')} a month or {planPrice('annual', 'cad')} a year.</>}
      </p>

      {/* The full set, once. Everything /compare claims for Chaptery belongs here — the
          assistant, the export formats, the writing dashboard and the browser access
          were all missing before, which meant the page understated what is being sold.
          (#790) */}
      <section className="pricing-includes">
        <h2>Every plan includes</h2>
        {/* jsx-a11y calls this redundant, and on an ordinary <ul> it would be. This one
            sets list-style: none, which makes Safari drop list semantics entirely — so
            VoiceOver stops announcing "list, 12 items" and the shape of what is included
            is lost. The explicit role puts it back. */}
        {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
        <ul className="pricing-includes-list" role="list">
          <li>A distraction-free desktop editor for Windows and macOS</li>
          <li>Your bookshelf, world, storyboard and dashboard in any browser</li>
          <li>World builder — characters, locations and events</li>
          <li>A visual relationship graph of your whole cast</li>
          <li>Storyboard canvas for plotting the beats</li>
          {/* Says outright that the key is yours. Discovering that after subscribing
              would feel like a bait and switch, and running it locally is a genuine
              selling point for anyone who does not want their draft sent anywhere. */}
          <li>An AI assistant that works on your manuscript — bring your own API key, or run it locally</li>
          <li>Writing goals, streaks and a 30-day history</li>
          <li>Book design and typesetting, then export to print-ready PDF, EPUB or Word</li>
          <li>Revision history for every version of your book</li>
          {/* Carries the asterisk because the footnote below explains the storage terms.
              Dropping this line while keeping "Unlimited books*" left the asterisk
              pointing at a claim the page no longer made. */}
          <li>Cloud sync, backup and unlimited storage*</li>
          <li>One world reused across a whole series</li>
          <li>Free, continuous updates</li>
        </ul>
      </section>

      <p className="pricing-footnote">
        * Unlimited books and storage are intended for legitimate writing project use. Individual file uploads are subject to a size limit designed to accommodate even the largest manuscripts. Abusive use — including non-book content, excessive book counts, or synthetic data — may result in account suspension per our <a href="/tos" target="_blank" rel="noopener noreferrer">Terms of Service</a>.
      </p>

      <section className="pricing-faq">
        <h3>Frequently asked questions</h3>

        <div className="pricing-faq-item">
          <strong>What do I get during the free trial?</strong>
          <p>Full access to every Chaptery feature — the desktop editor, world building, the relationship graph, the storyboard, the writing dashboard, the assistant, export and cloud sync — for <strong>one book</strong>, for 30 days. No credit card required. When you subscribe, you can create unlimited books.</p>
        </div>

        <div className="pricing-faq-item">
          <strong>What happens when my trial ends?</strong>
          <p>The desktop app switches to read-only mode with export available so you can always access your work and export it to your preferred format. Subscribe to unlock everything again. Your data is never deleted.</p>
        </div>

        <div className="pricing-faq-item">
          <strong>Which subscription is right for me?</strong>
          <p>If you know you'll be using Chaptery long-term, the <strong>Annual</strong> plan saves you two months compared to paying month-to-month. If you'd prefer flexibility, <strong>Monthly</strong> lets you cancel any time.</p>
        </div>

        <div className="pricing-faq-item">
          <strong>Can I cancel anytime?</strong>
          <p>Yes. Cancel from your account page and you keep full access through the end of your current billing period. No partial refunds, no cancellation fees.</p>
        </div>
      </section>
    </div>
    </>
  );
};

export default PricingPage;
